import CollectionLoader, { CollectionLoadedListener } from "../../General/CollectionLoader";
import { NotificationChatData } from "./NotificationDataTypes/NotificationChatData";
import { NotificationData } from "./NotificationData";
import { NotificationDeviationActivityData } from "./NotificationDataTypes/NotificationDeviationActivityData";
import { NotificationDeviationReminderData } from "./NotificationDataTypes/NotificationDeviationReminderData";
import { NotificationNewChatData } from "./NotificationDataTypes/NotificationNewChatData";
import { NotificationNewDeviationData } from "./NotificationDataTypes/NotificationNewDeviationData";
import { NotificationTaskActivityData } from "./NotificationDataTypes/NotificationTaskActivityData";
import { NotificationEventAttendeeData } from "./NotificationDataTypes/NotificationEventAttendeeData";
import { NotificationPostData } from "./NotificationDataTypes/NotificationPostData";
import { NotificationActivityData } from "./NotificationDataTypes/NotificationActivityData";
import { NotificationGeneralLinkData } from "./NotificationDataTypes/NotificationGeneralLinkData";
import { collection, getFirestore, onSnapshot } from "firebase/firestore";

export class UserNotifications extends CollectionLoader<NotificationData> {
  override setupRealtimeListener(onUpdate: (docs: NotificationData[]) => void) {
    const path = this.getPath();
    const listener: CollectionLoadedListener<NotificationData> = new CollectionLoadedListener(onUpdate, this);
    this.loadedListeners.push(listener);

    if (this.realtimeListeners.length === 0) {
      this.realtimeListeners.push(
        onSnapshot(
          collection(getFirestore(), path),
          (query) => {
            // Removed docs
            for (const doc of query.docChanges()) {
              if (doc.type === "removed") {
                this.removeData(doc.doc.id);
              }
            }

            // Added/updated
            const docs = query.docs.filter((doc) => this.collection === "trashcan" || !doc.data().deletedBy);

            for (const doc of docs) {
              const docData = doc.data();
              const data = this.addData(doc.id, docData);
              data.setup(docData);
            }

            this.loaded = true;
            this.loadedListeners.forEach((listener) => {
              listener.callback(this.documents);
            });
          },
          (error) => {
            console.error("Error setting up realtime listener for " + path);
            console.error(error.message);
            this.loaded = true;
            this.loadedListeners.forEach((listener) => {
              listener.callback(this.documents);
            });
          }
        )
      );
    }

    return listener;
  }

  override addData(id: string, data?: { [key: string]: any }): NotificationData {
    const existing = this.documents.find((doc) => doc.id === id);
    if (existing) {
      return existing;
    }

    const type = typeof data?.type === "number" ? data.type : NotificationData.types.UNDEFINED;

    const document = this.getNotificationDataForType(id, type);
    document.parent = this.parent;
    this.documents.push(document);
    return document;
  }

  private getNotificationDataForType(id: string, type: number): NotificationData {
    const types = NotificationData.types;
    let notification: NotificationData;

    switch (type) {
      case types.NEW_DEVIATION:
        notification = new NotificationNewDeviationData(id, this.collection, this.parent);
        break;
      case types.DEVIATION_ACTIVITY:
        notification = new NotificationDeviationActivityData(id, this.collection, this.parent);
        break;
      case types.NEW_CHAT:
        notification = new NotificationNewChatData(id, this.collection, this.parent);
        break;
      case types.CHAT_MESSAGE:
        notification = new NotificationChatData(id, this.collection, this.parent);
        break;
      case types.TASK_ACTIVITY:
        notification = new NotificationTaskActivityData(id, this.collection, this.parent);
        break;
      case types.DEVIATION_REMINDER:
        notification = new NotificationDeviationReminderData(id, this.collection, this.parent);
        break;
      case types.NEW_EVENT_ATTENDEE:
        notification = new NotificationEventAttendeeData(id, this.collection, this.parent);
        break;
      case types.NEW_POST:
        notification = new NotificationPostData(id, this.collection, this.parent);
        break;
      case types.ACTIVITY:
        notification = new NotificationActivityData(id, this.collection, this.parent);
        break;
      case types.GENERAL_LINK:
        notification = new NotificationGeneralLinkData(id, this.collection, this.parent);
        break;

      //This handles notification-type: UNDEFINED
      default:
        notification = new NotificationData(id, this.collection, this.parent);
        break;
    }

    return notification;
  }
}

